import { formatActor } from "./api-formatting-nIGXUFdx.js";
import { z } from "zod";

//#region src/tools/catalog/support/uptime-monitors.ts
/** Verified against UptimeSubscription.IntervalSeconds in getsentry/sentry. */
const UPTIME_INTERVAL_SECONDS = [
	60,
	300,
	600,
	1200,
	1800,
	3600
];
/** Verified against UptimeSubscription.SupportedHTTPMethods in getsentry/sentry. */
const UPTIME_HTTP_METHODS = [
	"GET",
	"POST",
	"HEAD",
	"PUT",
	"DELETE",
	"PATCH",
	"OPTIONS"
];
const UPTIME_MONITOR_STATUSES = ["active", "disabled"];
const ParamUptimeIntervalSeconds = z.number().int().refine((value) => UPTIME_INTERVAL_SECONDS.includes(value), { message: `intervalSeconds must be one of: ${UPTIME_INTERVAL_SECONDS.join(", ")}` }).describe("Seconds between checks. Allowed values: 60, 300, 600, 1200, 1800, 3600.");
const ParamUptimeHttpMethod = z.enum(UPTIME_HTTP_METHODS).describe("HTTP method used for the uptime check request.");
const ParamUptimeMonitorStatus = z.enum(UPTIME_MONITOR_STATUSES).describe("Monitor status. `disabled` stops checks and does not count against quota.");
const ParamUptimeTimeoutMs = z.number().int().min(1e3).max(6e4).describe("Request timeout in milliseconds (1000-60000).");
const ParamUptimeHeaders = z.array(z.tuple([z.string(), z.string()])).describe("Optional HTTP headers as an array of [name, value] pairs, e.g. [[\"Accept\",\"application/json\"]].");
/** Owner actor for create/update. Empty/whitespace rejected. */
const ParamUptimeOwner = z.string().trim().min(1).regex(/^(user|team):.+$/, "owner must use `user:ID` or `team:ID` format (e.g. `user:123` or `team:456`).").describe("Owner actor in `user:ID` or `team:ID` format.");
const SENSITIVE_HEADER_NAMES = new Set([
	"authorization",
	"proxy-authorization",
	"cookie",
	"set-cookie",
	"x-api-key",
	"x-auth-token",
	"x-access-token",
	"x-csrf-token",
	"x-sentry-auth"
]);
function isSensitiveHeaderName(name) {
	const normalized = name.trim().toLowerCase();
	if (SENSITIVE_HEADER_NAMES.has(normalized)) return true;
	return normalized.includes("authorization") || normalized.includes("api-key") || normalized.includes("access-token") || normalized.includes("secret") || normalized.includes("password");
}
/**
* Format headers for tool output without leaking secrets.
* Malformed short arrays are skipped. Sensitive values are redacted.
*/
function formatUptimeHeadersForOutput(headers) {
	if (!Array.isArray(headers) || headers.length === 0) return [];
	const lines = [];
	for (const header of headers) {
		if (!Array.isArray(header) || header.length < 2) continue;
		const name = header[0];
		const value = header[1];
		if (typeof name !== "string" || typeof value !== "string") continue;
		const displayValue = isSensitiveHeaderName(name) ? "[REDACTED]" : value;
		lines.push(`- ${name}: ${displayValue}`);
	}
	return lines;
}
const uptimeStatusSchema = z.enum([
	"ok",
	"failed",
	"unknown"
]);
/** Compact list item for discovery. Omits config detail agents rarely need in lists. */
const uptimeMonitorListItemSchema = z.object({
	id: z.string(),
	name: z.string(),
	projectSlug: z.string(),
	status: z.string(),
	uptimeStatus: uptimeStatusSchema,
	url: z.string(),
	method: z.string().optional(),
	intervalSeconds: z.number(),
	environment: z.string().optional(),
	owner: z.string().optional(),
	webUrl: z.string().url()
});
/**
* Mutation/detail summary: list fields plus config knobs useful after create/update.
* Optional fields are omitted when absent instead of returned as null.
*/
const uptimeMonitorSummarySchema = uptimeMonitorListItemSchema.extend({
	timeoutMs: z.number(),
	recoveryThreshold: z.number().optional(),
	downtimeThreshold: z.number().optional(),
	traceSampling: z.boolean().optional(),
	responseCaptureEnabled: z.boolean().optional()
});
function getUptimeOwnerName(monitor) {
	if (!monitor.owner) return null;
	const formatted = formatActor(monitor.owner);
	return formatted === "unknown" ? null : formatted;
}
function formatUptimeStatus(value) {
	if (value === 1 || value === "1" || value === "ok") return "ok";
	if (value === 2 || value === "2" || value === "failed") return "failed";
	return "unknown";
}
function optionalString(value) {
	if (value === void 0 || value === null) return void 0;
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : void 0;
}
function toUptimeMonitorListItem(monitor, webUrl) {
	return {
		id: String(monitor.id),
		name: monitor.name,
		projectSlug: monitor.projectSlug,
		status: monitor.status,
		uptimeStatus: formatUptimeStatus(monitor.uptimeStatus),
		url: monitor.url,
		...monitor.method ? { method: monitor.method } : {},
		intervalSeconds: monitor.intervalSeconds,
		...optionalString(monitor.environment) ? { environment: optionalString(monitor.environment) } : {},
		...getUptimeOwnerName(monitor) ? { owner: getUptimeOwnerName(monitor) } : {},
		webUrl
	};
}
function toUptimeMonitorSummary(monitor, webUrl) {
	return {
		...toUptimeMonitorListItem(monitor, webUrl),
		timeoutMs: monitor.timeoutMs,
		...monitor.recoveryThreshold !== void 0 && monitor.recoveryThreshold !== null ? { recoveryThreshold: monitor.recoveryThreshold } : {},
		...monitor.downtimeThreshold !== void 0 && monitor.downtimeThreshold !== null ? { downtimeThreshold: monitor.downtimeThreshold } : {},
		...monitor.traceSampling !== void 0 && monitor.traceSampling !== null ? { traceSampling: monitor.traceSampling } : {},
		...monitor.responseCaptureEnabled !== void 0 && monitor.responseCaptureEnabled !== null ? { responseCaptureEnabled: monitor.responseCaptureEnabled } : {}
	};
}

//#endregion
export { ParamUptimeHeaders, ParamUptimeHttpMethod, ParamUptimeIntervalSeconds, ParamUptimeMonitorStatus, ParamUptimeOwner, ParamUptimeTimeoutMs, UPTIME_HTTP_METHODS, UPTIME_INTERVAL_SECONDS, UPTIME_MONITOR_STATUSES, formatUptimeHeadersForOutput, formatUptimeStatus, getUptimeOwnerName, isSensitiveHeaderName, toUptimeMonitorListItem, toUptimeMonitorSummary, uptimeMonitorListItemSchema, uptimeMonitorSummarySchema, uptimeStatusSchema };
//# sourceMappingURL=uptime-monitors-DdMrkrKy.js.map